import { clsx, type ClassValue } from "clsx";
import { twMerge } from "tailwind-merge";
import { parse, format, isValid } from "date-fns";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

/**
 * Does this string carry an explicit timezone (a trailing Z, or +/-HH:MM)?
 *
 * Matters because `new Date("2026-09-10T04:06:00")` — no marker — is read as
 * LOCAL time, not UTC. A server sending naive UTC therefore has every timestamp
 * silently shifted by the reader's offset: ten hours in Sydney, twelve in
 * Auckland. Nothing looks broken, the times are just wrong.
 */
export const hasExplicitTimezone = (value: string): boolean =>
  /(?:Z|[+-]\d{2}:?\d{2})$/i.test(value.trim());

/**
 * A date with no time at all — "2026-09-10" or "10.09.2026".
 *
 * These are not moments. The backend currently stamps chat messages with one of
 * these, which is why every message sent on the same day showed an identical
 * time: there was no time to show, so one was invented.
 */
export const isDateOnly = (value: string): boolean =>
  /^\d{4}-\d{2}-\d{2}$/.test(value.trim()) || /^\d{2}[./-]\d{2}[./-]\d{4}$/.test(value.trim());

/**
 * Turn whatever the server sent into a Date, without guessing wrongly.
 *
 * Order matters. `new Date()` accepts "10.09.2026" and reads it as US
 * month-first, turning 10 September into 9 October — so day-first formats must
 * be tried BEFORE falling back to the built-in parser, not after.
 */
function toDate(dateInput: string | Date): Date | null {
  if (dateInput instanceof Date) return isValid(dateInput) ? dateInput : null;
  const raw = String(dateInput).trim();
  if (raw === "") return null;

  // Epoch seconds or milliseconds arriving as a bare number.
  if (/^\d{10}$/.test(raw)) return new Date(Number(raw) * 1000);
  if (/^\d{13}$/.test(raw)) return new Date(Number(raw));

  // A plain calendar date must be read as a LOCAL date. `new Date("2026-09-10")`
  // is defined as UTC midnight, which in any negative-offset timezone renders
  // as the PREVIOUS day — a message sent on the 10th showing as the 9th.
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (ymd) {
    return new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
  }

  // Day-first formats before the built-in parser — see the note above about
  // 10.09.2026. Separators kept in step with isDateOnly, so a value classed as
  // a date is also parsed as one.
  for (const pattern of [
    "dd.MM.yyyy HH:mm:ss",
    "dd.MM.yyyy HH:mm",
    "dd.MM.yyyy",
    "dd/MM/yyyy HH:mm:ss",
    "dd/MM/yyyy HH:mm",
    "dd/MM/yyyy",
    "dd-MM-yyyy HH:mm:ss",
    "dd-MM-yyyy HH:mm",
    "dd-MM-yyyy",
  ]) {
    const parsed = parse(raw, pattern, new Date());
    if (isValid(parsed)) return parsed;
  }

  const parsed = new Date(raw);
  return isValid(parsed) ? parsed : null;
}

/**
 * Format a timestamp for display next to a message.
 *
 * Returns just the date when the server gave us only a date, rather than
 * inventing a time that looks precise and is wrong.
 */
export function formatTimestamp(dateInput: string | Date): string {
  try {
    const raw = typeof dateInput === "string" ? dateInput.trim() : "";
    const date = toDate(dateInput);

    if (date === null) return String(dateInput);

    // A date with no time is not a moment. Showing "05:00" for it is a
    // fabrication, and showing the same fabricated time on every message that
    // day is what made the chat look broken.
    if (raw !== "" && isDateOnly(raw)) return format(date, "MMM d, yyyy");

    // Note: a timestamp with no timezone marker is read as LOCAL time by the
    // JS engine, so a naive UTC value from the server displays shifted by the
    // reader's offset. That cannot be corrected here — the server has to send
    // an explicit timezone. hasExplicitTimezone() flags which values are safe.
    return format(date, "HH:mm , MMM d, yyyy");
  } catch (error) {
    console.error("Error formatting timestamp:", error);
    return String(dateInput);
  }
}
